import React, { useState, useEffect } from 'react';
import { Edit, Save, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ImageUploader from '../../components/admin/ImageUploader';
import axios from 'axios';

const HeroManager = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/hero-slides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlides(response.data.slides);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (isCreating) {
        // Create new slide
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/hero-slides`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Nouveau slide créé avec succès!');
      } else {
        // Update existing slide
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/hero-slides/${editing}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Slide mis à jour avec succès!');
      }
      setEditing(null);
      setIsCreating(false);
      setFormData({});
      fetchSlides();
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce slide?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/hero-slides/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Slide supprimé!');
      fetchSlides();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const startCreating = () => {
    const nextOrder = slides.length > 0 ? Math.max(...slides.map(s => s.order)) + 1 : 1;
    setFormData({
      title: '',
      title_en: '',
      subtitle: '',
      subtitle_en: '',
      image: '',
      order: nextOrder
    });
    setIsCreating(true);
    setEditing('new');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Hero Slider</h1>
          <p className="text-gray-600 mt-1">Gérez les images du slider de la page d'accueil</p>
        </div>
        <Button onClick={startCreating} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un Slide
        </Button>
      </div>

      {isCreating && (
        <Card className="border-2 border-purple-500">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Plus className="w-5 h-5 text-purple-600" />
              <h3 className="font-bold text-lg">Nouveau Slide #{formData.order}</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <ImageUploader
                  value={formData.image || ''}
                  onChange={(url) => setFormData({ ...formData, image: url })}
                  label="Image du Slide"
                />
              </div>
              <div>
                <Label>Titre (FR)</Label>
                <Input value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
              </div>
              <div>
                <Label>Title (EN)</Label>
                <Input value={formData.title_en || ''} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} />
              </div>
              <div>
                <Label>Sous-titre (FR)</Label>
                <Input value={formData.subtitle || ''} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
              </div>
              <div>
                <Label>Subtitle (EN)</Label>
                <Input value={formData.subtitle_en || ''} onChange={(e) => setFormData({ ...formData, subtitle_en: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="bg-green-600"><Save className="w-4 h-4 mr-2" />Créer</Button>
              <Button onClick={() => { setIsCreating(false); setEditing(null); setFormData({}); }} variant="outline">Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {slides.map((slide) => (
          <Card key={slide.id}>
            {editing === slide.id ? (
              <CardContent className="pt-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <ImageUploader
                      value={formData.image || ''}
                      onChange={(url) => setFormData({ ...formData, image: url })}
                      label="Image du Slide"
                    />
                  </div>
                  <div>
                    <Label>Titre (FR)</Label>
                    <Input value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Title (EN)</Label>
                    <Input value={formData.title_en || ''} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} />
                  </div>
                  <div>
                    <Label>Sous-titre (FR)</Label>
                    <Input value={formData.subtitle || ''} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
                  </div>
                  <div>
                    <Label>Subtitle (EN)</Label>
                    <Input value={formData.subtitle_en || ''} onChange={(e) => setFormData({ ...formData, subtitle_en: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} className="bg-green-600"><Save className="w-4 h-4 mr-2" />Sauvegarder</Button>
                  <Button onClick={() => setEditing(null)} variant="outline">Annuler</Button>
                </div>
              </CardContent>
            ) : (
              <div className="flex items-center gap-6 p-6">
                <div className="relative w-48 h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {slide.image ? (
                    <img 
                      src={slide.image.startsWith('http') ? slide.image : `${process.env.REACT_APP_BACKEND_URL}${slide.image}`} 
                      alt={slide.title} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded">Slide {slide.order}</span>
                  </div>
                  <h3 className="font-bold text-lg">{slide.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{slide.subtitle}</p>
                  {slide.title_en && (
                    <p className="text-sm text-gray-500 mt-2 italic">EN: {slide.title_en}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setEditing(slide.id); setFormData(slide); }}>
                    <Edit className="w-4 h-4 mr-2" />Modifier
                  </Button>
                  {slides.length > 1 && (
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(slide.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Conseils</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Utilisez des images haute résolution (1920x1080px minimum)</li>
          <li>• Pour de meilleures images, allez dans <strong>Bibliothèque</strong> pour uploader vos photos</li>
          <li>• Les changements sont visibles immédiatement sur le site</li>
          <li>• Format recommandé: JPG ou PNG</li>
        </ul>
      </div>
    </div>
  );
};

export default HeroManager;
